import type { UseFetchOptions } from 'nuxt/app'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFAQPageSchema } from './schemaOrg/useFAQPageSchema'
import useSeoHelper from './useSeoHelper'
import type { StrapiApiDefaultResponse } from '~/composables/types/strapi-api'
import { dynamicContentTransformer } from '~/utils/dynamicContentTransformer'

export function useStrapiPage({
  slug,
  options = {},
  locale = 'de-DE',
}: {
  slug: string
  options?: UseFetchOptions<StrapiApiDefaultResponse<any[]>, any[]>
  locale?: string
}) {
  const config = useRuntimeConfig()
  const preview = useRoute().query.preview === 'true'
  const { strapiGraphqlURL, strapiDraftGraphqlURL, strapiApiKey } =
    config.public
  const baseURL = preview ? strapiDraftGraphqlURL : strapiGraphqlURL

  const getQuery = (slug: string) => ({
    operationName: null,
    variables: {
      status: preview ? 'DRAFT' : 'PUBLISHED',
    },
    query: getStrapiGraphqlPageQuery(slug, locale),
  })

  const staticMetrics = useStaticMetrics()
  const defaults: UseFetchOptions<StrapiApiDefaultResponse<any[]>, any[]> = {
    baseURL,
    key: baseURL + slug,
    method: 'POST',
    body: getQuery(slug),
    headers: {
      Authorization: `Bearer ${strapiApiKey}`,
      'Content-Type': 'application/json',
    },
  }

  const baseTransform = (data: StrapiApiDefaultResponse<any>) => {
    if (options.transform)
      return dynamicContentTransformer(
        options.transform(data.data),
        staticMetrics,
        locale
      )
    return dynamicContentTransformer(data.data, staticMetrics, locale)
  }

  const params = { ...defaults, ...options, transform: baseTransform }

  return useFetch(strapiGraphqlURL, params)
}

export function useStrapiPageSeo({
  pageData = {},
  schemas,
}: {
  pageData: any
  schemas?: any[]
}) {
  const { t } = useI18n()
  if (!pageData) return

  const getSeoData = computed(() => {
    return pageData?.data?.value?.pages?.[0]?.contents.find(
      (item: any) => item.__typename === 'ComponentSharedSeo'
    )
  })

  if (!getSeoData.value) return

  const title = getSeoData.value.metaTitle || t('general.titles.base')

  // Find all FAQ sections in the page content
  const faqSections = computed(() => {
    const contents = pageData?.data?.value?.pages?.[0]?.contents ?? []
    return contents.filter((block: any) => {
      return block.__typename === 'ComponentSharedFaqSection'
    })
  })

  // Extract all questions from FAQ sections
  const faqQuestions = computed(() => {
    return faqSections.value.flatMap((section: any, sectionIndex: number) => {
      if (section.questions && Array.isArray(section.questions)) {
        return section.questions.map((q: any, index: number) => ({
          id: q.id || `faq-section-${sectionIndex}-q-${index}`,
          question: q.question,
          answer: q.answer,
        }))
      }
      return []
    })
  })

  // Add FAQ schema if there are questions
  if (faqQuestions.value && faqQuestions.value.length > 0) {
    useFAQPageSchema(faqQuestions.value)
  }

  const seoOptions = computed(() =>
    getSeoData.value
      ? {
          head: {
            title,
            ...(schemas && { script: schemas }),
          },
          meta: {
            title,
            'og:title': title,
            description: getSeoData.value.metaDescription || '',
            'og:description': getSeoData.value.metaDescription || '',
            'og:image': getSeoData.value.metaImage?.data?.url,
          },
        }
      : {}
  )
  useSeoHelper(seoOptions)
}
